import { useState, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, Download, AudioLines, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

export default function AudioBar({ getSelection }) {
  const { t } = useI18n();
  const [voices, setVoices] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [charLimit, setCharLimit] = useState(2500);
  const [voiceId, setVoiceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/audio/voices").then(({ data }) => {
      setVoices(data.voices || []);
      setConfigured(data.configured);
      setCharLimit(data.char_limit || 2500);
      if (data.voices?.length) setVoiceId(data.voices[0].voice_id);
    }).catch(() => {});
  }, []);

  const selText = () => {
    const sel = getSelection();
    return (sel.text || sel.full || "").slice(0, charLimit);
  };
  const usedChars = selText().length;

  const generate = async () => {
    const text = selText();
    if (!text.trim()) { toast.error(t("audio.willUse")); return; }
    setLoading(true);
    setAudioUrl(null);
    try {
      const { data } = await api.post("/audio/tts", { text, voice_id: voiceId });
      setAudioUrl(data.audio_url);
      setTimeout(() => { audioRef.current?.play(); }, 100);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  if (!configured) {
    return (
      <div className="flex items-center gap-2 text-xs text-amber-400/90 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="audio-not-configured">
        <TriangleAlert className="w-4 h-4 shrink-0" /> {t("audio.notConfigured")}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="audio-player-toggle">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold">
        <AudioLines className="w-3.5 h-3.5" /> {t("audio.title")}
      </span>

      <Select value={voiceId} onValueChange={setVoiceId}>
        <SelectTrigger className="h-8 w-[210px] bg-ink-800 border-white/10 text-xs" data-testid="audio-voice-select">
          <SelectValue placeholder={t("audio.voice")} />
        </SelectTrigger>
        <SelectContent className="bg-ink-800 border-white/10 text-slate-100 max-h-64">
          {voices.map((v) => (
            <SelectItem key={v.voice_id} value={v.voice_id} className="text-xs">{v.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <button
        onClick={audioUrl ? togglePlay : generate}
        disabled={loading}
        data-testid="audio-play-button"
        className="inline-flex items-center gap-1.5 rounded-full bg-gold hover:bg-gold-light text-ink-900 font-semibold px-4 py-1.5 text-xs transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : audioUrl ? (playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />) : <Play className="w-3.5 h-3.5" />}
        {loading ? t("audio.generating") : t("audio.play")}
      </button>

      {audioUrl && (
        <a
          href={audioUrl}
          download="eneser-audio.mp3"
          data-testid="audio-download-button"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:text-gold hover:border-gold/40 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> {t("audio.download")}
        </a>
      )}

      <span className={`text-[11px] font-mono ml-auto ${usedChars >= charLimit ? "text-amber-400" : "text-slate-500"}`}>
        {usedChars.toLocaleString()} / {charLimit.toLocaleString()}
      </span>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      )}
    </div>
  );
}
